/**
 * UI Styles
 * Centralized CSS styles for SDK components
 */

import { Z_INDEX, DIMENSIONS } from '../constants/sdk-constants';

/**
 * Color scheme for dark mode support
 */
export interface ColorScheme {
  inputBgColor: string;
  shimmerBase: string;
  shimmerLight: string;
}

/**
 * Get color scheme based on dark mode preference
 */
export function getColorScheme(isDarkMode: boolean): ColorScheme {
  return {
    inputBgColor: isDarkMode ? '#2d2d2d' : '#F5F5F5',
    shimmerBase: isDarkMode ? '#3d3d3d' : '#E5E5E5',
    shimmerLight: isDarkMode ? '#4d4d4d' : '#F0F0F0',
  };
}

/**
 * Get shimmer gradient for skeleton loader
 */
export function getShimmerGradient(colors: ColorScheme): string {
  return `linear-gradient(90deg, ${colors.shimmerBase} 25%, ${colors.shimmerLight} 50%, ${colors.shimmerBase} 75%)`;
}

/**
 * Overlay styles for popup mode
 */
export function getOverlayStyles(): string {
  return `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: ${Z_INDEX.OVERLAY};
  `;
}

/**
 * Container styles for popup mode
 */
export function getContainerStyles(): string {
  return `
    position: relative;
    width: ${DIMENSIONS.CONTAINER_WIDTH_PERCENT};
    max-width: ${DIMENSIONS.CONTAINER_MAX_WIDTH};
    height: ${DIMENSIONS.CONTAINER_HEIGHT_PERCENT};
    max-height: ${DIMENSIONS.CONTAINER_MAX_HEIGHT};
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  `;
}

/**
 * Close button styles
 */
export function getCloseButtonStyles(): string {
  return `
    position: absolute;
    top: 10px;
    right: 10px;
    width: ${DIMENSIONS.BUTTON_SIZE}px;
    height: ${DIMENSIONS.BUTTON_SIZE}px;
    border: none;
    background: transparent;
    font-size: 24px;
    cursor: pointer;
    z-index: ${Z_INDEX.MODAL};
    color: #333;
    display: flex;
    align-items: center;
    justify-content: center;
  `;
}

/**
 * Iframe styles for popup mode
 */
export function getPopupIframeStyles(): string {
  return `
    width: 100%;
    height: 100%;
    border: none;
    border-radius: 8px;
  `;
}

/**
 * Iframe styles for inline mode
 */
export function getInlineIframeStyles(fillParent: boolean): string {
  const width = fillParent ? '100%' : DIMENSIONS.DEFAULT_IFRAME_WIDTH;
  const maxWidth = fillParent ? 'none' : '100%';
  
  return `
    width: ${width};
    max-width: ${maxWidth};
    height: ${DIMENSIONS.MIN_HEIGHT}px;
    min-height: ${DIMENSIONS.MIN_HEIGHT}px;
    border: none;
    display: block;
    transition: height 0.2s ease;
  `;
}

/**
 * Loader container styles
 */
export function getLoaderContainerStyles(): string {
  return `
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: ${Z_INDEX.LOADER};
    padding: 20px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
  `;
}

/**
 * Skeleton card element styles
 */
export function getSkeletonCardStyles(fillParent: boolean): string {
  const width = fillParent ? '100%' : DIMENSIONS.DEFAULT_IFRAME_WIDTH;
  
  return `
    width: ${width};
    max-width: 100%;
    margin: 0 auto;
  `;
}

/**
 * Input wrapper styles for skeleton loader
 */
export function getInputWrapStyles(bgColor: string): string {
  return `
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background-color: ${bgColor};
    padding: 8px;
    border-radius: 8px;
    margin-bottom: 20px;
  `;
}

/**
 * Skeleton element base styles
 */
export function getSkeletonBaseStyles(shimmerGradient: string): string {
  return `
    background: ${shimmerGradient};
    background-size: 200% 100%;
    animation: inflowpay-shimmer 1.5s infinite;
  `;
}

/**
 * Card number skeleton styles
 */
export function getCardNumberSkeletonStyles(shimmerGradient: string): string {
  return `
    flex: 1;
    min-width: 0;
    height: 32px;
    border-radius: 6px;
    ${getSkeletonBaseStyles(shimmerGradient)}
  `;
}

/**
 * Expiry skeleton styles
 */
export function getExpirySkeletonStyles(shimmerGradient: string): string {
  return `
    width: 21.5%;
    flex-shrink: 0;
    height: 32px;
    border-radius: 6px;
    ${getSkeletonBaseStyles(shimmerGradient)}
  `;
}

/**
 * CVC skeleton styles
 */
export function getCvcSkeletonStyles(shimmerGradient: string): string {
  return `
    width: 17.5%;
    flex-shrink: 0;
    height: 32px;
    border-radius: 6px;
    ${getSkeletonBaseStyles(shimmerGradient)}
  `;
}

/**
 * Button skeleton styles
 */
export function getButtonSkeletonStyles(shimmerGradient: string): string {
  return `
    width: 100%;
    height: 42px;
    border-radius: 8px;
    ${getSkeletonBaseStyles(shimmerGradient)}
    margin-bottom: 16px;
  `;
}

/**
 * Disclaimer skeleton container styles
 */
export function getDisclaimerSkeletonStyles(): string {
  return `
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    width: 100%;
    margin-top: 16px;
  `;
}

/**
 * Disclaimer icon skeleton styles
 */
export function getDisclaimerIconSkeletonStyles(shimmerGradient: string): string {
  return `
    width: 10px;
    height: 10px;
    border-radius: 50%;
    ${getSkeletonBaseStyles(shimmerGradient)}
  `;
}

/**
 * Disclaimer text skeleton styles
 */
export function getDisclaimerTextSkeletonStyles(shimmerGradient: string): string {
  return `
    width: 80%;
    height: 16px;
    border-radius: 4px;
    ${getSkeletonBaseStyles(shimmerGradient)}
  `;
}

/**
 * Shimmer animation keyframes
 */
export const SHIMMER_ANIMATION = `
  @keyframes inflowpay-shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

/**
 * 3DS overlay styles
 */
export function get3DSOverlayStyles(): string {
  return `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: ${Z_INDEX.OVERLAY};
  `;
}

/**
 * 3DS modal styles
 */
export function get3DSModalStyles(): string {
  return `
    position: relative;
    width: ${DIMENSIONS.CONTAINER_WIDTH_PERCENT};
    max-width: ${DIMENSIONS.CONTAINER_MAX_WIDTH};
    height: ${DIMENSIONS.CONTAINER_HEIGHT_PERCENT};
    max-height: ${DIMENSIONS.CONTAINER_MAX_HEIGHT};
    background: white;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    display: flex;
    flex-direction: column;
  `;
}

/**
 * 3DS header styles
 */
export function get3DSHeaderStyles(): string {
  return `
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 15px 20px;
    border-bottom: 1px solid #e5e5e5;
  `;
}

/**
 * 3DS content styles
 */
export function get3DSContentStyles(): string {
  return `
    flex: 1;
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
  `;
}

/**
 * 3DS iframe styles
 */
export function get3DSIframeStyles(): string {
  return `
    width: 100%;
    height: 100%;
    border: none;
  `;
}
